import useMediaQuery from '@lib/hooks/useMediaQuery'
import { selectAlpha2Code } from '@redux/globalSlice'
import clsx from 'clsx'
import { DetailedHTMLProps, FC, HTMLAttributes, useRef } from 'react'
import { useSelector } from 'react-redux'

import {
  HeaderDropdownFragment,
  HeaderLinkV2Fragment,
} from '@lib/umbraco/generated/types.generated'
import { isTouch } from '@utils/helpers/DeviceUtils'
import invariant from 'tiny-invariant'
import Link from '../Link/Link'
import NavItemContentElement from './NavItemContentElement/NavItemContentElement'
import s from './Navbar.module.scss'

export type NavbarDropdownProps = (
  | HeaderDropdownFragment
  | HeaderLinkV2Fragment
) & {
  onMenuItemButtonClick: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void
} & DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>
const NavItem: FC<NavbarDropdownProps> = ({
  className,
  onMenuItemButtonClick,
  __typename,
  style,
  ...props
}) => {
  const isBreakpointWithMenu = useMediaQuery('(max-width: 1280px)')
  const flyoutRef = useRef<HTMLDivElement>(null)
  const alpha2Code = useSelector(selectAlpha2Code)

  if (__typename === 'HeaderLinkV2') {
    const { link } = props as HeaderLinkV2Fragment
    return (
      <div className={clsx(className, {})} ref={flyoutRef} style={style}>
        <div className={s.submenuTriggerGroup}>
          <div className={s.submenuTriggerItem}>
            <Link link={link} className={clsx(s.link, s.submenuTriggerLink)}>
              <span className={clsx(s.linkText, s.underlineAnimation)}>
                {link.name}
              </span>
              <span className={s.linkChevron} style={{ visibility: 'hidden' }}>
                <svg
                  height="48"
                  viewBox="0 0 9 48"
                  width="9"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="m1.5618 24.0621 6.5581-6.4238c.2368-.2319.2407-.6118.0088-.8486-.2324-.2373-.6123-.2407-.8486-.0088l-7 6.8569c-.1157.1138-.1807.2695-.1802.4316.001.1621.0674.3174.1846.4297l7 6.7241c.1162.1118.2661.1675.4155.1675.1577 0 .3149-.062.4326-.1846.2295-.2388.2222-.6187-.0171-.8481z"></path>
                </svg>
              </span>
            </Link>
          </div>
        </div>
      </div>
    )
  }
  const { label, subMenus, dropdownContent, link } =
    props as HeaderDropdownFragment
  if (label === 'Shop' && alpha2Code === 'INT') {
    return <></>
  }
  return (
    <div
      className={clsx(className, s.itemSubmenu, {})}
      ref={flyoutRef}
      style={style}
    >
      <ul className={s.submenuTriggerGroup} onClick={onMenuItemButtonClick}>
        <li className={s.submenuTriggerItem}>
          <Link
            className={clsx(s.link, s.submenuTriggerLink)}
            link={link}
            onClick={(e) =>
              (isTouch() || isBreakpointWithMenu) && e.preventDefault()
            }
          >
            <span className={clsx(s.linkText, s.underlineAnimation)}>
              {label}
            </span>
            <span className={s.linkChevron}>
              <svg
                height="48"
                viewBox="0 0 9 48"
                width="9"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="m1.5618 24.0621 6.5581-6.4238c.2368-.2319.2407-.6118.0088-.8486-.2324-.2373-.6123-.2407-.8486-.0088l-7 6.8569c-.1157.1138-.1807.2695-.1802.4316.001.1621.0674.3174.1846.4297l7 6.7241c.1162.1118.2661.1675.4155.1675.1577 0 .3149-.062.4326-.1846.2295-.2388.2222-.6187-.0171-.8481z"></path>
              </svg>
            </span>
          </Link>
        </li>
        <li className={s.submenuTriggerItem}>
          <button className={s.submenuTriggerButton}></button>
        </li>
      </ul>
      {__typename == 'HeaderDropdown' && (
        <div className={clsx(s.submenu, s.flyout)}>
          <div className={s.flyoutScrollContainer} data-scroll>
            <div className={clsx(s.submenuContent, s.flyoutContent)}>
              <div className={s.submenuContentLeft}>
                {subMenus?.map((section, index) => {
                  invariant(
                    section.content.__typename === 'NavigationSubMenuGroup',
                    'Invalid section type',
                  )
                  const header = section.content.links.find((x) => x)
                  return (
                    <div className={s.submenuGroup} key={index}>
                      <p className={s.submenuHeader}>
                        <Link link={header} className={s.submenuHeaderLink}>
                          <span
                            className={clsx(
                              s.submenuHeaderLinkText,
                              s.underlineAnimation,
                            )}
                          >
                            {header.name}
                          </span>
                        </Link>
                      </p>
                      <ul className={s.submenuList}>
                        {section.content.links.slice(1).map((link, index) => (
                          <li key={index} className={s.submenuListItem}>
                            <Link link={link} className={s.submenuLink}>
                              <span
                                className={clsx(
                                  s.submenuLinkText,
                                  s.underlineAnimation,
                                )}
                              >
                                {link.name}
                              </span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )
                })}
              </div>
              <div className={s.submenuContentRight}>
                {dropdownContent?.map((dropdownContent, index) => {
                  invariant(
                    dropdownContent.content.__typename ===
                      'HeaderDropdownContentElement',
                    'Invalid section type',
                  )
                  return (
                    <NavItemContentElement
                      key={index}
                      {...dropdownContent.content}
                    />
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default NavItem
